import { useState } from "react";
import { Button } from "primereact/button";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { Editor, defaultEditorModules } from "../Editor";
import { TagListItem } from "../../api/types/tag";
import {
  ContentBoxSectionEnum,
  CreateContentBoxPayload,
  UpdateContentBoxPayload,
} from "../../api/types/contentBox";
import { useMutation, useQueryClient } from "react-query";
import { createProfileContentBoxRequest } from "../../api/queries/contentBoxes/create";
import { updateContentBoxRequest } from "../../api/queries/contentBoxes/update";
import { deleteContentBoxRequest } from "../../api/queries/contentBoxes/delete";
import { ContentBoxListItem } from "../../api/types/contentBox";

interface Props {
  uuid: string;
  section: ContentBoxSectionEnum;
  contentBoxes: ContentBoxListItem[];
  availableTags: TagListItem[];
}

export const ContentBoxList = ({
  uuid,
  section,
  contentBoxes,
  availableTags,
}: Props) => {
  const queryClient = useQueryClient();
  const [content, setContent] = useState<string>("");
  const [tags, setTags] = useState<TagListItem[]>([]);
  const [editId, setEditId] = useState<string | null>(null);

  const createBox = useMutation({
    mutationFn: (payload: CreateContentBoxPayload) =>
      createProfileContentBoxRequest(section, uuid, payload),
    onSuccess: () => {
      queryClient.invalidateQueries(["content-boxes", section, uuid]);
      resetForm();
    },
  });

  const updateBox = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateContentBoxPayload;
    }) => updateContentBoxRequest(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries(["content-boxes", section, uuid]);
      resetForm();
    },
  });

  const deleteBox = useMutation({
    mutationFn: (id: string) => deleteContentBoxRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["content-boxes", section, uuid]);
    },
  });

  const onSubmit = async () => {
    const payload = {
      value: content,
      tags: (tags || []).map((tag) => tag.id),
    };

    if (editId) {
      await updateBox.mutateAsync({ id: editId, payload });
    } else {
      await createBox.mutateAsync(payload);
    }
  };

  const onEdit = (box: ContentBoxListItem) => {
    setEditId(box.id);
    setContent(box.value);
    setTags(box.content_box_tags);
  };
  const onDelete = async (id: string) => {
    await deleteBox.mutateAsync(id);
  };

  const resetForm = () => {
    setContent("");
    setTags([]);
    setEditId(null);
  };

  return (
    <div className="mt-4">
      <h3>Content Boxes</h3>

      {contentBoxes.map((box) => (
        <div
          key={box.id || box.value.slice(0, 10)}
          className="p-3 border-1 surface-border border-round mb-3 w-full"
        >
          <div
            dangerouslySetInnerHTML={{ __html: box.value }}
            className="mb-2"
          />

          <div className="flex flex-wrap gap-1 mb-2">
            {(box.content_box_tags || []).map((tag) => (
              <span key={tag.id} className="p-chip p-chip-text">
                {tag.name}
              </span>
            ))}
          </div>

          <Button
            type="button"
            icon="pi pi-pencil"
            className="p-button-text p-button-sm mr-2"
            onClick={() => onEdit(box)}
            tooltip="Edit"
          />
          <Button
            type="button"
            icon="pi pi-trash"
            className="p-button-text p-button-sm"
            onClick={() => onDelete(box.id)}
            tooltip="Remove"
          />
        </div>
      ))}

      <div>
        <Editor
          content={content}
          onChange={(value) => setContent(value)}
          modules={defaultEditorModules}
          placeholder="Enter content"
        />

        <MultiSelect
          value={tags}
          onChange={(e: MultiSelectChangeEvent) =>
            setTags(e.value as TagListItem[])
          }
          options={availableTags}
          optionLabel="name"
          display="chip"
          placeholder="Select tags"
          maxSelectedLabels={3}
          className="w-full mt-2"
          filter={true}
          filterBy="name"
        />

        <Button
          type="button"
          label={editId ? "Update Content Box" : "Add New Content Box"}
          icon={editId ? "pi pi-refresh" : "pi pi-plus"}
          onClick={onSubmit}
          className="p-button-sm mt-2"
        />
      </div>
    </div>
  );
};
